import React, { useCallback, useContext, useEffect, useState } from 'react';

import { Accordion, AccordionDetails, AccordionSummary, Grid } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import moment from 'moment';

import { GlobalContext } from '../../../../../../store';
import { IRoundGroup } from '../../../../../../store/admin/eventRounds/store';
import { ITeam } from '../../../../../../store/admin/eventTeams/store';
import { IAdminTeamGame } from '../../../../../../store/admin/teamGames/store';

import {
  useTeeSheetCreatorDispatch,
  useTeeSheetCreatorState
} from '../../Context/TeeSheetCreator.context';
import { useRouteInfoState } from '../../../../../../components/RouteInfo/Context/RouteInfo.context';
import {
  getObjectByIdFromArray,
  IObjectById
} from '../../../../../../contrib/Utils/getObjectByIdFromArray';
import { AddNewTeamCard } from './AddNewTeamCard';
import { TeamCard } from '../../../../../../components/TeamCard/TeamCard';
import { GroupStyled } from '../Group/Group.styles';
import { useShotgunOptions } from '../Group/useShotgunOptions';
import Select from '../../../../../../components/AutoComplete';

interface IProps {
  group: IRoundGroup;
}

const TeamGroup = (props: IProps) => {
  const { group } = props;

  const {
    adminTeamGames,
    adminEventRounds: { current: currentRound },
    adminEventTeams
  } = useContext(GlobalContext);

  const { eventId } = useRouteInfoState();
  const { openGroups, selectedTeams } = useTeeSheetCreatorState();
  const {
    onShotgunOptionSelected,
    shotgunOptionsFormatted,
    isShotgun,
    groupShotgunSpot
  } = useShotgunOptions(group);

  const { dispatch } = useTeeSheetCreatorDispatch();

  const [groupStart, setGroupStart] = useState('');
  const [groupTeamGames, setGroupTeamGames] = useState<IAdminTeamGame[]>([]);
  const [groupTeams, setGroupTeams] = useState<IObjectById<ITeam>>({});
  const [playersPerGroup, setPlayersPerGroup] = useState<number>();
  const [theresATeamThatFits, setTheresATeamThatFits] = useState(true);
  const [totalGroupPlayers, setTotalGroupPlayers] = useState<number>();

  useEffect(() => {
    setGroupStart(moment(group.start).format('HH:mm'));
  }, [group]);

  useEffect(() => {
    if (currentRound && currentRound.playersPerTee) {
      setPlayersPerGroup(currentRound.playersPerTee);
    }
  }, [currentRound]);

  useEffect(() => {
    if (adminTeamGames[group.id]) {
      setGroupTeamGames(adminTeamGames[group.id]);
    }
  }, [adminTeamGames, group.id, adminTeamGames[group.id]]);

  useEffect(() => {
    const groupTeamsIds = groupTeamGames.map(teamGame => teamGame.team);

    const foundTeams = adminEventTeams[Number(eventId)]
      ? adminEventTeams[Number(eventId)].filter(eventTeam => groupTeamsIds.includes(eventTeam.id))
      : [];

    if (foundTeams.length !== Object.keys(groupTeams).length) {
      setGroupTeams(getObjectByIdFromArray(foundTeams));
    }
  }, [adminTeamGames, group.id, adminEventTeams, eventId, groupTeamGames]);

  useEffect(() => {
    const foundTeams = Object.values(groupTeams);

    setTotalGroupPlayers(
      foundTeams.reduce(
        (playersAcc, team) =>
          team.enrollments ? playersAcc + team.enrollments.length : playersAcc,
        0
      )
    );
  }, [adminEventTeams, groupTeams, Object.values(groupTeams).length, eventId]);

  const onToggle = useCallback(() => {
    dispatch({
      type: 'set',
      value: {
        attribute: 'openGroups',
        value: { ...openGroups, [group.id]: !openGroups[group.id] }
      }
    });
  }, [openGroups, group]);

  useEffect(() => {
    if (adminEventTeams[Number(eventId)]) {
      const remainingPlayers = Number(playersPerGroup) - Number(totalGroupPlayers);

      setTheresATeamThatFits(
        adminEventTeams[Number(eventId)].some(
          team =>
            team.isValid &&
            !selectedTeams.includes(team.id) &&
            team.enrollments.length <= remainingPlayers
        )
      );
    }
  }, [group, selectedTeams, totalGroupPlayers, playersPerGroup, adminEventTeams, eventId]);

  return (
    <GroupStyled>
      <Accordion expanded={!!openGroups[group.id]} className="group-panel" onChange={onToggle}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} className="group-header">
          {isShotgun ? (
            <Select
              defaultValue={groupShotgunSpot}
              placeholder="Select"
              onChange={onShotgunOptionSelected}
              color="primary"
              options={shotgunOptionsFormatted}
            />
          ) : (
            groupStart
          )}

          <div className="circle-slots">
            {new Array(playersPerGroup).fill('empty').map((_, index) => (
              <div
                className={`circle-slot ${
                  totalGroupPlayers && totalGroupPlayers > index ? 'filled' : ''
                }`}
                key={`circle-slot-${group.id}-${index}`}
              />
            ))}
          </div>
        </AccordionSummary>

        <AccordionDetails className="expansion-panel-details group">
          <Grid container={true} spacing={2} className="grid-container">
            {groupTeamGames.map(teamGame =>
              groupTeams[teamGame.team] ? (
                <Grid
                  key={`teamGroup-teamCard-${teamGame.id}`}
                  className="grid-item"
                  item={true}
                  xs={12}
                  md={6}
                >
                  <TeamCard context="tee" team={groupTeams[teamGame.team]} teamGame={teamGame} />
                </Grid>
              ) : (
                <div key={`teamGroup-teamCard-${teamGame.id}`} />
              )
            )}

            {theresATeamThatFits && (
              <Grid className="grid-item" item={true} xs={12} md={6}>
                <div className="team-side left-players">
                  <AddNewTeamCard group={group} />
                </div>
              </Grid>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
    </GroupStyled>
  );
};

export { TeamGroup };
